/**
 * AuthModal - global Sign-in / Sign-up / Forgot-password popup.
 *
 * Triggered via the `useAuthModal()` context. Lives at the App root so
 * any marketing page can pop it without a route change.
 *
 * Calls Supabase directly (does NOT depend on the workspace AuthProvider,
 * which is scoped under /workspace/*). Same flows as the dedicated
 * /workspace/login and /workspace/signup pages, just in a modal shell with
 * a close button + ESC + click-outside dismiss.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft, Loader2 } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { getSupabase, isSupabaseConfigured } from '../../workspace/lib/supabaseClient';
import { useAuthModal } from '../../context/AuthModalContext';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import CaptchaGate from './CaptchaGate';
import { ease, dur } from '../../lib/motion';
import './AuthModal.css';

const GoogleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4" />
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853" />
    <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05" />
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335" />
  </svg>
);

const AppleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.42 2.21-1.18 3.05-.93 1.04-2.42 1.85-3.59 1.76-.15-1.13.42-2.31 1.13-3.07.84-.9 2.31-1.62 3.64-1.74zM21.34 17.04c-.55 1.27-.81 1.84-1.52 2.97-.99 1.57-2.39 3.53-4.13 3.55-1.55.01-1.95-.96-4.05-.95-2.1.01-2.54.97-4.1.95-1.74-.02-3.07-1.78-4.06-3.35C.91 16.6-.05 11.79 2.06 8.63c1.49-2.22 3.85-3.52 6.07-3.52 2.26 0 3.69 1.16 5.55 1.16 1.81 0 2.92-1.16 5.54-1.16 1.98 0 4.08 1.04 5.58 2.84-4.91 2.55-4.11 9.4.54 9.09z" />
  </svg>
);

const oauthRedirectTo = () =>
  typeof window !== 'undefined'
    ? `${window.location.origin}/workspace/auth/callback`
    : undefined;

const AuthModal = () => {
  const { isOpen, mode, redirectTo, returnY, close, setMode } = useAuthModal();
  const navigate = useNavigate();
  const configured = isSupabaseConfigured();
  const reduced = useReducedMotion();

  // form state - shared across modes
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [pwScore, setPwScore] = useState(0);
  const captchaRef = useRef(null);
  const MIN_PW_SCORE = 3; // zxcvbn "Strong" or better
  const [submitting, setSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null); // 'google' | 'apple' | null

  // 2026-02-18 Stage 2: MFA challenge state. After a successful password
  // sign-in, if the user has TOTP enrolled Supabase puts the session at
  // AAL1; we need to challenge for a verified code to escalate to AAL2.
  // Challenge step renders inline in the same modal.
  const [mfaChallenge, setMfaChallenge] = useState(null); // { factorId, challengeId } | null
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRecoveryMode, setMfaRecoveryMode] = useState(false);
  const [mfaRecoveryCode, setMfaRecoveryCode] = useState('');

  // 2026-02-18 Stage 2.5 (c): After a successful sign-up or sign-in we offer
  // first-time users a one-tap passkey enrolment so the next visit is just
  // Face ID / Touch ID. Tracked here as an inline modal step so we never
  // navigate away until the user picks "Set up now" or "Maybe later".
  const [passkeyPrompt, setPasskeyPrompt] = useState(null); // null | { dest:string }
  const [passkeyEnrolling, setPasskeyEnrolling] = useState(false);
  const [passkeyDoneMsg, setPasskeyDoneMsg] = useState(null);

  // 2026-02-21 Phase 1 (auth simplification): Magic-link is now the
  // PRIMARY sign-in path. Email-only field at the top of the modal;
  // a single tap sends a single-use signed link to the user's inbox
  // and skips passwords entirely. Password is still available as the
  // "Use password instead" fallback for users who prefer it.
  // Internal-app OTP gates (lead-gate matcher, etc.) are unchanged -
  // this only affects account login/signup.
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkSending, setMagicLinkSending] = useState(false);

  const sendMagicLink = async () => {
    setError(null);
    setInfo(null);
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setMagicLinkSending(true);
    try {
      const supabase = getSupabase();
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: oauthRedirectTo(),
          // Only allow sign-up via this path when the user is on the
          // signup tab. Defaults to true (existing accounts work; new
          // accounts get auto-created at first link click) on signup,
          // and false on signin so an unknown email returns an error
          // instead of silently creating an account.
          shouldCreateUser: mode === 'signup',
        },
      });
      if (err) throw err;
      setMagicLinkSent(true);
    } catch (e) {
      setError(e?.message || 'Could not send sign-in link');
    } finally {
      setMagicLinkSending(false);
    }
  };

  // Reset form when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setInfo(null);
      setOauthLoading(null);
      setSubmitting(false);
      setMagicLinkSent(false);
      setMagicLinkSending(false);
    }
  }, [isOpen, mode]);

  // ESC to close (uses safeClose so a 2FA-mid-challenge dismiss signs out)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') safeClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, mfaChallenge]);

  // Defensive scroll preservation. Some triggers (notably real
  // browser clicks on sticky-header buttons) cause the browser to
  // scroll the page to top BEFORE this effect runs. The
  // AuthModalContext captures the scroll position at the moment of
  // `open()` and passes it as `returnY` so we can restore it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const targetY = returnY ?? window.scrollY;
    const restore = () => {
      if (window.scrollY !== targetY) {
        const html = document.documentElement;
        const prev = html.style.scrollBehavior;
        html.style.scrollBehavior = 'auto';
        window.scrollTo(0, targetY);
        requestAnimationFrame(() => { html.style.scrollBehavior = prev; });
      }
    };
    // Restore immediately, then on next paint, then after focus settles.
    restore();
    const r1 = requestAnimationFrame(() => { restore(); requestAnimationFrame(restore); });
    const t1 = setTimeout(restore, 50);
    const t2 = setTimeout(restore, 200);
    return () => { cancelAnimationFrame(r1); clearTimeout(t1); clearTimeout(t2); };
  }, [isOpen, returnY]);

  if (!isOpen) return null;

  const goAfterAuth = () => {
    close();
    navigate(redirectTo || '/workspace/dashboard');
  };

  // 2026-02-18 Stage 2.5 (c): Decide whether to interrupt the navigation with
  // a "save Face ID for next time?" prompt. We skip when:
  //  - the device has no WebAuthn (window.PublicKeyCredential missing)
  //  - the user has dismissed the prompt before (localStorage flag)
  //  - the user already has at least 1 passkey enrolled
  // Returns true if the prompt was opened (and the caller should NOT navigate).
  // 2026-02-21 Phase 2 (auth simplification): the post-signin passkey
  // enrolment prompt is now NEVER auto-shown. Forcing users through a
  // third modal right after they signed in was the largest single
  // friction point in the flow. Passkey enrolment lives in
  // Profile -> Security where users can opt-in on their own terms.
  // The helper below is kept (returning false) so callers that still
  // expect a boolean don't break - but it always falls through to
  // goAfterAuth() now.
  const maybeOfferPasskeyPrompt = async (/* dest */) => false;

  // 2026-02-21 Phase 3: After any successful sign-in, drop a 30-day
  // trusted-device cookie. The backend mints a signed HMAC bound to
  // user_id + UA hash + IP /16, so future sign-ins from the same
  // browser can skip the confirmation challenges that would otherwise
  // fire on high-risk actions. Best-effort: failures (no session, no
  // network, secret not configured) are swallowed - trust just doesn't
  // get set, which means the user gets the (more secure) full challenge
  // flow next time.
  const trustThisDevice = async () => {
    try {
      const supabase = getSupabase();
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) return;
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/trust-device`, {
        method: 'POST',
        credentials: 'include', // critical so the Set-Cookie sticks
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Best-effort; never block the sign-in flow on this.
    }
  };

  const goAfterAuthWithPasskeyOffer = async (dest) => {
    const opened = await maybeOfferPasskeyPrompt(dest);
    if (opened) return;
    // Fire-and-forget trust-device call. We don't await it because the
    // user is already authenticated; the cookie just appears for the
    // next request once the response lands.
    trustThisDevice();
    if (dest) {
      close();
      navigate(dest);
    } else {
      goAfterAuth();
    }
  };

  // Enrol a passkey inline (without leaving the modal). Uses the same flow as
  // the Profile-page PasskeySection but optimised for a single happy path.
  const enrollPasskeyFromPrompt = async () => {
    if (!window.PublicKeyCredential) {
      setPasskeyDoneMsg('This device does not support passkeys.');
      return;
    }
    setError(null);
    setPasskeyEnrolling(true);
    try {
      const supabase = getSupabase();
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('Session lost. Please sign in again.');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

      const beginR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/passkeys/register/begin`, {
        method: 'POST', headers,
      });
      if (!beginR.ok) {
        const j = await beginR.json().catch(() => ({}));
        throw new Error(j?.detail || 'Could not start passkey setup');
      }
      const { options: optsJson } = await beginR.json();
      const opts = JSON.parse(optsJson);

      const b64uToBytes = (s) => {
        const pad = '='.repeat((4 - (s.length % 4)) % 4);
        const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
      };
      const bytesToB64u = (buf) => {
        const b = new Uint8Array(buf);
        let s = '';
        for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
        return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      };
      opts.challenge = b64uToBytes(opts.challenge);
      opts.user.id = b64uToBytes(opts.user.id);
      if (opts.excludeCredentials) {
        opts.excludeCredentials = opts.excludeCredentials.map((c) => ({ ...c, id: b64uToBytes(c.id) }));
      }

      const cred = await navigator.credentials.create({ publicKey: opts });

      const finishR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/passkeys/register/finish`, {
        method: 'POST', headers,
        body: JSON.stringify({
          friendly_name: 'This device',
          credential: {
            id: cred.id,
            rawId: bytesToB64u(cred.rawId),
            type: cred.type,
            response: {
              attestationObject: bytesToB64u(cred.response.attestationObject),
              clientDataJSON: bytesToB64u(cred.response.clientDataJSON),
            },
          },
        }),
      });
      if (!finishR.ok) {
        const j = await finishR.json().catch(() => ({}));
        throw new Error(j?.detail || 'Passkey setup failed');
      }
      setPasskeyEnrolling(false);
      setPasskeyDoneMsg('Passkey saved. Next time, just tap Face ID.');
      // Brief pause so the user reads the success, then continue.
      setTimeout(() => {
        const dest = passkeyPrompt?.dest || '/workspace/dashboard';
        setPasskeyPrompt(null);
        setPasskeyDoneMsg(null);
        close();
        navigate(dest);
      }, 1100);
    } catch (e) {
      setPasskeyEnrolling(false);
      if (e?.name === 'NotAllowedError') {
        setPasskeyDoneMsg('Cancelled. You can set this up later from your Profile.');
      } else {
        setPasskeyDoneMsg(e?.message || 'Could not save passkey. Continue without it for now.');
      }
    }
  };

  const dismissPasskeyPrompt = (remember) => {
    if (remember && typeof window !== 'undefined' && window.localStorage) {
      try { window.localStorage.setItem('bp_passkey_prompt_dismissed', '1'); } catch { /* noop */ }
    }
    const dest = passkeyPrompt?.dest || '/workspace/dashboard';
    setPasskeyPrompt(null);
    setPasskeyDoneMsg(null);
    close();
    navigate(dest);
  };

  const handleOAuth = async (provider) => {
    if (!configured) return;
    setError(null);
    setOauthLoading(provider);
    try {
      const supabase = getSupabase();
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: oauthRedirectTo() },
      });
      if (oauthError) {
        setError(oauthError.message || `Could not sign in with ${provider}.`);
        setOauthLoading(null);
      }
      // On success the browser navigates away.
    } catch (e) {
      setError(e?.message || `Could not sign in with ${provider}.`);
      setOauthLoading(null);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!configured) return;
    setError(null);
    setSubmitting(true);
    try {
      // Captcha gate - blocks bot brute-force before we even hit Supabase.
      const captchaOk = await captchaRef.current?.verify?.('signin');
      if (captchaOk === false) {
        setSubmitting(false);
        setError('Please complete the security check, then try again.');
        return;
      }
      const supabase = getSupabase();
      const { data, error: err } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      setSubmitting(false);
      if (err) {
        captchaRef.current?.reset?.();
        setError(err.message || 'Could not sign in.');
        return;
      }
      if (data?.session) {
        // 2026-02-27: MFA is off by default until we ship a proper opt-in
        // UI inside the user account settings. Until then we skip the
        // AAL2 challenge entirely - any user with a previously-enrolled
        // TOTP factor (e.g. early dogfooders) can still sign in cleanly.
        // To re-enable the gate, flip MFA_GATE_ENABLED back to true.
        const MFA_GATE_ENABLED = false;
        const challenged = MFA_GATE_ENABLED ? await startMfaChallengeIfNeeded() : false;
        if (!challenged) await goAfterAuthWithPasskeyOffer();
      }
    } catch (err) {
      setSubmitting(false);
      captchaRef.current?.reset?.();
      setError(err?.message || 'Could not sign in.');
    }
  };

  // 2026-02-18 Stage 2: MFA challenge state. If a user with MFA enabled
  // signs in, Supabase puts the session at AAL1; we need to challenge for
  // a verified TOTP code to get to AAL2. The challenge step renders inline
  // in the same modal.
  const startMfaChallengeIfNeeded = async () => {
    try {
      const supabase = getSupabase();
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData?.nextLevel === 'aal2' && aalData.currentLevel !== 'aal2') {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totp = (factors?.totp || []).find((f) => f.status === 'verified');
        if (totp) {
          const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
            factorId: totp.id,
          });
          if (!chErr && ch) {
            // Capture user id so the optional passkey step can resolve creds.
            const { data: u } = await supabase.auth.getUser();
            setMfaChallenge({ factorId: totp.id, challengeId: ch.id, userId: u?.user?.id });
            return true;
          }
        }
      }
    } catch { /* fall through, treat as no-mfa */ }
    return false;
  };

  // 2026-02-18 Stage 2.5: Face ID / Touch ID alternative for the 2FA step.
  // We trust this only as a "passkey unlocked" signal AFTER the user has
  // already passed password. The MFA challenge itself is still satisfied
  // via Supabase's TOTP verify (using the recovery-code endpoint as the
  // server-trusted gate) once the passkey assertion succeeds.
  const tryPasskeyAtMfaStep = async () => {
    if (!mfaChallenge?.userId) {
      setError('Could not start Face ID - try the code instead.');
      return;
    }
    if (!window.PublicKeyCredential) {
      setError('This device does not support passkeys.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      // Begin assertion
      const beginR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/passkeys/authenticate/begin`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: mfaChallenge.userId }),
      });
      if (!beginR.ok) {
        const j = await beginR.json().catch(() => ({}));
        throw new Error(j?.detail || 'No passkey available');
      }
      const { options: optsJson } = await beginR.json();
      const opts = JSON.parse(optsJson);
      // b64u -> bytes
      const b64uToBytes = (s) => {
        const pad = '='.repeat((4 - (s.length % 4)) % 4);
        const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
      };
      const bytesToB64u = (buf) => {
        const b = new Uint8Array(buf);
        let s = '';
        for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
        return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      };
      opts.challenge = b64uToBytes(opts.challenge);
      if (opts.allowCredentials) {
        opts.allowCredentials = opts.allowCredentials.map((c) => ({ ...c, id: b64uToBytes(c.id) }));
      }
      const assertion = await navigator.credentials.get({ publicKey: opts });
      // Finish
      const finishR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/passkeys/authenticate/finish`, {
        method: 'POST', headers,
        body: JSON.stringify({
          user_id: mfaChallenge.userId,
          credential: {
            id: assertion.id,
            rawId: bytesToB64u(assertion.rawId),
            type: assertion.type,
            response: {
              authenticatorData: bytesToB64u(assertion.response.authenticatorData),
              clientDataJSON: bytesToB64u(assertion.response.clientDataJSON),
              signature: bytesToB64u(assertion.response.signature),
              userHandle: assertion.response.userHandle ? bytesToB64u(assertion.response.userHandle) : null,
            },
          },
        }),
      });
      setSubmitting(false);
      if (!finishR.ok) {
        const j = await finishR.json().catch(() => ({}));
        throw new Error(j?.detail || 'Face ID verification failed');
      }
      // Passkey verified - clear MFA gate locally and continue. The
      // Supabase session stays at AAL1 but our app-level gate is unlocked.
      setMfaChallenge(null);
      setMfaCode('');
      goAfterAuth();
    } catch (e) {
      setSubmitting(false);
      if (e?.name === 'NotAllowedError') {
        setError('Cancelled - try again or use the 6-digit code.');
      } else {
        setError(e?.message || 'Face ID failed - use the 6-digit code instead.');
      }
    }
  };

  // 2026-02-18 Stage 2.5 (b): Passwordless passkey sign-in from the email-only
  // state. The user types their email, taps "Use Face ID / Passkey", we ask
  // the backend for an assertion challenge, browser triggers Face ID, then
  // we exchange the verified assertion for a Supabase magic-link token and
  // call supabase.auth.verifyOtp() to materialise a real session.
  const handlePasskeyLogin = async () => {
    if (!window.PublicKeyCredential) {
      setError('This device does not support passkeys.');
      return;
    }
    const cleanEmail = (email || '').trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes('@')) {
      setError('Type your email first, then tap Face ID.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      // 1. Begin assertion.
      const beginR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/passkeys/login/begin`, {
        method: 'POST', headers,
        body: JSON.stringify({ email: cleanEmail }),
      });
      if (!beginR.ok) {
        const j = await beginR.json().catch(() => ({}));
        throw new Error(j?.detail || 'No passkey is set up for this email');
      }
      const { options: optsJson } = await beginR.json();
      const opts = JSON.parse(optsJson);

      const b64uToBytes = (s) => {
        const pad = '='.repeat((4 - (s.length % 4)) % 4);
        const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
        return out;
      };
      const bytesToB64u = (buf) => {
        const b = new Uint8Array(buf);
        let s = '';
        for (let i = 0; i < b.length; i++) s += String.fromCharCode(b[i]);
        return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      };
      opts.challenge = b64uToBytes(opts.challenge);
      if (opts.allowCredentials) {
        opts.allowCredentials = opts.allowCredentials.map((c) => ({ ...c, id: b64uToBytes(c.id) }));
      }

      // 2. Browser Face ID / Touch ID prompt.
      const assertion = await navigator.credentials.get({ publicKey: opts });

      // 3. Send assertion back; backend verifies + mints a magiclink token.
      const finishR = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/passkeys/login/finish`, {
        method: 'POST', headers,
        body: JSON.stringify({
          email: cleanEmail,
          credential: {
            id: assertion.id,
            rawId: bytesToB64u(assertion.rawId),
            type: assertion.type,
            response: {
              authenticatorData: bytesToB64u(assertion.response.authenticatorData),
              clientDataJSON: bytesToB64u(assertion.response.clientDataJSON),
              signature: bytesToB64u(assertion.response.signature),
              userHandle: assertion.response.userHandle ? bytesToB64u(assertion.response.userHandle) : null,
            },
          },
        }),
      });
      if (!finishR.ok) {
        const j = await finishR.json().catch(() => ({}));
        throw new Error(j?.detail || 'Face ID sign-in failed');
      }
      const { token_hash: tokenHash } = await finishR.json();
      if (!tokenHash) throw new Error('Server did not return a session token');

      // 4. Exchange the magic-link token for a real Supabase session.
      const supabase = getSupabase();
      const { error: otpErr } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: 'magiclink',
      });
      setSubmitting(false);
      if (otpErr) {
        setError(otpErr.message || 'Face ID sign-in could not complete.');
        return;
      }
      goAfterAuth();
    } catch (e) {
      setSubmitting(false);
      if (e?.name === 'NotAllowedError') {
        setError('Cancelled. Try again or use your password.');
      } else {
        setError(e?.message || 'Face ID sign-in failed - use your password instead.');
      }
    }
  };

  const submitMfaCode = async (e) => {
    e.preventDefault();
    if (!mfaChallenge) return;
    setError(null);
    setSubmitting(true);
    try {
      const supabase = getSupabase();
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: mfaChallenge.factorId,
        challengeId: mfaChallenge.challengeId,
        code: mfaCode.trim(),
      });
      setSubmitting(false);
      if (vErr) {
        setError('That code didn\u2019t match. Try again.');
        return;
      }
      setMfaChallenge(null);
      setMfaCode('');
      await goAfterAuthWithPasskeyOffer();
    } catch (err) {
      setSubmitting(false);
      setError(err?.message || 'Could not verify code.');
    }
  };

  const submitRecoveryCode = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const supabase = getSupabase();
      const sess = await supabase.auth.getSession();
      const token = sess?.data?.session?.access_token;
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/recovery-codes/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: mfaRecoveryCode.trim() }),
      });
      setSubmitting(false);
      if (!r.ok) {
        setError('That recovery code is invalid or already used.');
        return;
      }
      // Recovery code accepted - we treat the AAL1 session as sufficient.
      // (User should regenerate codes + re-verify TOTP from settings after.)
      setMfaChallenge(null);
      setMfaCode('');
      setMfaRecoveryCode('');
      setMfaRecoveryMode(false);
      await goAfterAuthWithPasskeyOffer();
    } catch (err) {
      setSubmitting(false);
      setError(err?.message || 'Could not verify recovery code.');
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!configured) return;
    setError(null);
    setInfo(null);
    if (!agree) {
      setError('Please accept the Terms and Privacy Policy to continue.');
      return;
    }
    if (password.length < 10) {
      setError('Please use a password with at least 10 characters.');
      return;
    }
    if (pwScore < MIN_PW_SCORE) {
      setError('Please pick a stronger password (aim for "Strong" or better).');
      return;
    }
    setSubmitting(true);
    try {
      const captchaOk = await captchaRef.current?.verify?.('signup');
      if (captchaOk === false) {
        setSubmitting(false);
        setError('Please complete the security check, then try again.');
        return;
      }
      const supabase = getSupabase();
      const { data, error: err } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: oauthRedirectTo(),
          data: {
            full_name: fullName.trim(),
            first_name: fullName.trim().split(' ')[0] || '',
            signup_source: 'web_modal',
          },
        },
      });
      setSubmitting(false);
      if (err) {
        captchaRef.current?.reset?.();
        setError(err.message || 'Could not create account.');
        return;
      }
      if (data?.session) {
        await goAfterAuthWithPasskeyOffer('/workspace/onboarding');
      } else {
        setInfo("Check your email to confirm your account, then sign in.");
      }
    } catch (err) {
      setSubmitting(false);
      captchaRef.current?.reset?.();
      setError(err?.message || 'Could not create account.');
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!configured) return;
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const captchaOk = await captchaRef.current?.verify?.('forgot');
      if (captchaOk === false) {
        setSubmitting(false);
        setError('Please complete the security check, then try again.');
        return;
      }
      const supabase = getSupabase();
      const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: oauthRedirectTo(),
      });
      setSubmitting(false);
      if (err) {
        captchaRef.current?.reset?.();
        setError(err.message || 'Could not send reset email.');
        return;
      }
      setInfo("Check your email for a reset link. It expires in 1 hour.");
    } catch (err) {
      setSubmitting(false);
      captchaRef.current?.reset?.();
      setError(err?.message || 'Could not send reset email.');
    }
  };

  // 2026-02-18 Stage 2.5 (e): Closing the modal during the 2FA challenge step
  // would leave the user with an AAL1 Supabase session - effectively bypassing
  // the second factor. Sign them out before closing so a dismissed challenge
  // is a clean "not signed in" state.
  const safeClose = async () => {
    if (mfaChallenge) {
      try {
        const supabase = getSupabase();
        await supabase.auth.signOut();
      } catch { /* ignore - closing anyway */ }
      setMfaChallenge(null);
      setMfaCode('');
      setMfaRecoveryCode('');
      setMfaRecoveryMode(false);
    }
    close();
  };

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) safeClose();
  };

  // ----- HEADERS PER MODE -----
  const headers = {
    signin:  { title: 'Welcome back',           sub: 'We\u2019ve built tools to help you navigate your building journey all in one place.' },
    signup:  { title: 'Create your account', sub: 'We\u2019ve built tools to help you navigate your building journey all in one place.' },
    forgot:  { title: 'Reset your password',    sub: 'We\u2019ll email you a secure link to set a new one.' },
  };
  const heading = headers[mode] || headers.signin;

  return (
    <motion.div
      className="bp-auth-modal-backdrop"
      onClick={onBackdrop}
      data-testid="auth-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-modal-title"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: reduced ? 0.15 : dur.short, ease: ease.out }}
    >
      <motion.div
        className="bp-auth-modal-card"
        data-testid="auth-modal"
        initial={reduced ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.985 }}
        animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: reduced ? 0.15 : dur.base, ease: ease.out, delay: reduced ? 0 : 0.04 }}
      >
        {/* Top bar: back arrow (forgot password only) + close × */}
        <div className="bp-auth-modal-top">
          {mode === 'forgot' ? (
            <button
              type="button"
              onClick={() => setMode('signin')}
              className="bp-auth-modal-back"
              aria-label="Back to sign in"
              data-testid="auth-modal-back"
            >
              <ArrowLeft size={18} />
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={safeClose}
            className="bp-auth-modal-close"
            aria-label="Close"
            data-testid="auth-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <img
          src="/images/buildpilot-logo-2026-dark.png"
          alt="BuildPilot"
          className="bp-auth-modal-logo"
          style={{ height: 24, width: 'auto', marginBottom: 14 }} loading="lazy" decoding="async" />

        <div className="bp-auth-modal-grid">
        <div className="bp-auth-modal-brand">
        <h2 id="auth-modal-title" className="bp-auth-modal-title">{heading.title}</h2>
        <p className="bp-auth-modal-sub">{heading.sub}</p>

        {/* Desktop-only value props - fills the brand column on
            wider screens so the 2-col layout feels intentional.
            Hidden on mobile via CSS. */}
        <ul className="bp-auth-modal-brand-points" aria-hidden="true">
          <li>
            <span className="bp-auth-modal-brand-point-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </span>
            Compare our Adelaide partner builders
          </li>
          <li>
            <span className="bp-auth-modal-brand-point-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </span>
            Browse 760+ home designs
          </li>
          <li>
            <span className="bp-auth-modal-brand-point-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </span>
            Decode 68 Adelaide suburbs
          </li>
          <li>
            <span className="bp-auth-modal-brand-point-icon" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            </span>
            One on One Support, on tap
          </li>
        </ul>
        </div>

        <div className="bp-auth-modal-body">

        {passkeyPrompt && (
          <div className="bp-auth-modal-passkey-prompt" data-testid="auth-modal-passkey-prompt">
            <div className="bp-auth-modal-passkey-prompt-icon" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11V8a3 3 0 0 1 6 0v3" />
                <rect x="5" y="11" width="14" height="9" rx="2" />
                <path d="M12 15v2" />
              </svg>
            </div>
            <h3 className="bp-auth-modal-passkey-prompt-title">Skip the password next time?</h3>
            <p className="bp-auth-modal-passkey-prompt-body">
              Save Face ID / Touch ID on this device for one-tap sign-in. Your password still works as a backup.
            </p>
            {passkeyDoneMsg && (
              <div className="bp-auth-modal-passkey-prompt-status" data-testid="auth-modal-passkey-prompt-status">
                {passkeyDoneMsg}
              </div>
            )}
            <button
              type="button"
              onClick={enrollPasskeyFromPrompt}
              disabled={passkeyEnrolling}
              className="bp-auth-modal-primary"
              data-testid="auth-modal-passkey-prompt-enroll"
            >
              {passkeyEnrolling ? <Loader2 className="bp-auth-modal-spin" size={16} /> : 'Set up Face ID / Passkey'}
            </button>
            <button
              type="button"
              onClick={() => dismissPasskeyPrompt(false)}
              disabled={passkeyEnrolling}
              className="bp-auth-modal-passkey-prompt-skip"
              data-testid="auth-modal-passkey-prompt-later"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={() => dismissPasskeyPrompt(true)}
              disabled={passkeyEnrolling}
              className="bp-auth-modal-passkey-prompt-never"
              data-testid="auth-modal-passkey-prompt-never"
            >
              Don&apos;t ask again
            </button>
          </div>
        )}

        {!passkeyPrompt && (<>
        {/* OAuth: not shown on forgot-password */}
        {mode !== 'forgot' && (
          <>
            <div className="bp-auth-modal-oauth">
              <button
                type="button"
                onClick={() => handleOAuth('google')}
                disabled={!configured || oauthLoading !== null}
                className="bp-auth-modal-oauth-btn bp-auth-modal-oauth-google"
                data-testid="auth-modal-google"
              >
                <GoogleIcon />
                {oauthLoading === 'google' ? 'Redirecting\u2026' : 'Continue with Google'}
              </button>
              <button
                type="button"
                onClick={() => handleOAuth('apple')}
                disabled={!configured || oauthLoading !== null}
                className="bp-auth-modal-oauth-btn bp-auth-modal-oauth-apple"
                data-testid="auth-modal-apple"
              >
                <AppleIcon />
                {oauthLoading === 'apple' ? 'Redirecting\u2026' : 'Continue with Apple'}
              </button>
            </div>
            <div className="bp-auth-modal-divider">
              <span>OR WITH EMAIL</span>
            </div>
          </>
        )}

        {mode === 'signin' && mfaChallenge && (
          <form onSubmit={mfaRecoveryMode ? submitRecoveryCode : submitMfaCode} noValidate data-testid="auth-modal-mfa-step">
            <div className="bp-auth-modal-info" style={{ marginBottom: 8 }}>
              {mfaRecoveryMode
                ? 'Enter one of your 10 recovery codes.'
                : 'Two-factor required. Enter the 6-digit code from your authenticator app.'}
            </div>
            <div className="bp-auth-modal-field">
              <label htmlFor="am-mfa">{mfaRecoveryMode ? 'Recovery code' : 'Verification code'}</label>
              <input
                id="am-mfa"
                type="text"
                inputMode={mfaRecoveryMode ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                required
                value={mfaRecoveryMode ? mfaRecoveryCode : mfaCode}
                onChange={(e) => {
                  const v = e.target.value;
                  if (mfaRecoveryMode) setMfaRecoveryCode(v.toUpperCase());
                  else setMfaCode(v.replace(/\D/g, '').slice(0, 6));
                }}
                placeholder={mfaRecoveryMode ? 'XXXX-XXXX' : '000000'}
                data-testid="auth-modal-mfa-input"
                style={{ letterSpacing: '0.25em', textAlign: 'center' }}
              />
            </div>
            {error && <div className="bp-auth-modal-error" data-testid="auth-modal-error">{error}</div>}
            <button
              type="submit"
              disabled={submitting || (mfaRecoveryMode ? mfaRecoveryCode.length < 8 : mfaCode.length < 6)}
              className="bp-auth-modal-primary"
              data-testid="auth-modal-mfa-submit"
            >
              {submitting ? <Loader2 className="bp-auth-modal-spin" size={16} /> : 'Verify and continue'}
            </button>
            <button
              type="button"
              onClick={tryPasskeyAtMfaStep}
              disabled={submitting}
              className="bp-auth-modal-link"
              data-testid="auth-modal-mfa-use-passkey"
              style={{ marginTop: 8 }}
            >
              Use Face ID / Touch ID instead
            </button>
            <button
              type="button"
              onClick={() => { setMfaRecoveryMode(!mfaRecoveryMode); setError(null); }}
              className="bp-auth-modal-link"
              data-testid="auth-modal-mfa-switch-mode"
              style={{ marginTop: 8 }}
            >
              {mfaRecoveryMode ? 'Use authenticator code instead' : 'I lost my authenticator - use a recovery code'}
            </button>
          </form>
        )}

        {mode === 'signin' && !mfaChallenge && (
          <>
            {/* 2026-02-21 Phase 1: Magic-link primary CTA. Sends a
                single-use signed sign-in link to the user's inbox.
                Password is still available below as the secondary
                option for users who prefer it. */}
            {magicLinkSent ? (
              <div className="bp-auth-modal-magic-sent" data-testid="auth-modal-magic-sent">
                <div className="bp-auth-modal-magic-sent-icon" aria-hidden="true">📬</div>
                <h3 className="bp-auth-modal-magic-sent-title">Check your inbox</h3>
                <p className="bp-auth-modal-magic-sent-body">
                  We sent a sign-in link to <strong>{email}</strong>. Click the link to sign in - no password needed.
                </p>
                <button
                  type="button"
                  onClick={() => setMagicLinkSent(false)}
                  className="bp-auth-modal-magic-sent-back"
                  data-testid="auth-modal-magic-sent-back"
                >
                  Use a different email or password instead
                </button>
              </div>
            ) : (
              <>
                <div className="bp-auth-modal-signin-stack">
                <div className="bp-auth-modal-magic">
                  <div className="bp-auth-modal-field">
                    <label htmlFor="am-magic-email">Email</label>
                    <input
                      id="am-magic-email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="auth-modal-magic-email"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={sendMagicLink}
                    disabled={magicLinkSending || !email}
                    className="bp-auth-modal-magic-cta"
                    data-testid="auth-modal-magic-cta"
                  >
                    {magicLinkSending ? 'Sending...' : 'Email me a sign-in link'}
                  </button>
                  <p className="bp-auth-modal-magic-help">
                    No password to remember. Single-use link, valid for 60 minutes.
                  </p>
                </div>

                <div className="bp-auth-modal-divider">
                  <span>or use password</span>
                </div>

                <form onSubmit={handleSignIn} noValidate>
            {/* 2026-02-21 Phase 1: Email field removed - the magic-link
                section above already captured `email` (same state). */}
            <input
              type="hidden"
              name="email"
              autoComplete="email"
              value={email}
              readOnly
            />
            <div className="bp-auth-modal-field">
              <label htmlFor="am-password">Password</label>
              <input
                id="am-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="auth-modal-password"
              />
            </div>
            <div className="bp-auth-modal-row-right">
              <button type="button" onClick={() => setMode('forgot')} className="bp-auth-modal-link" data-testid="auth-modal-forgot-link">
                Forgot password?
              </button>
            </div>
            {error && <div className="bp-auth-modal-error" data-testid="auth-modal-error">{error}</div>}
            <CaptchaGate ref={captchaRef} action="signin" />
            <button
              type="submit"
              disabled={submitting || !configured}
              className="bp-auth-modal-primary"
              data-testid="auth-modal-submit"
            >
              {submitting ? <Loader2 className="bp-auth-modal-spin" size={16} /> : 'Sign in'}
            </button>
            {typeof window !== 'undefined' && window.PublicKeyCredential && (
              <>
                <div className="bp-auth-modal-or-divider" aria-hidden="true">
                  <span>or</span>
                </div>
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={submitting || !configured}
                  className="bp-auth-modal-passkey-btn"
                  data-testid="auth-modal-passkey-login"
                  title="Sign in with the passkey saved on this device"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 11c0-2.5-2-4.5-4.5-4.5S3 8.5 3 11s2 4.5 4.5 4.5" />
                    <path d="M7.5 15.5h6l1 4-1.5 1.5H9l-1.5-1.5z" />
                    <circle cx="17" cy="9" r="3" />
                    <path d="M17 12v3" />
                  </svg>
                  Use Face ID / Passkey
                </button>
              </>
            )}
            <p className="bp-auth-modal-footer-text">
              New to BuildPilot?{' '}
              <button type="button" onClick={() => setMode('signup')} className="bp-auth-modal-switch" data-testid="auth-modal-switch-signup">
                Create an account
              </button>
            </p>
          </form>
          </div>
          </>
            )}
          </>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} noValidate>
            <div className="bp-auth-modal-field">
              <label htmlFor="am-name">Full name</label>
              <input
                id="am-name"
                type="text"
                autoComplete="name"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Sarah Johnson"
                data-testid="auth-modal-name"
              />
            </div>
            <div className="bp-auth-modal-field">
              <label htmlFor="am-email-su">Email</label>
              <input
                id="am-email-su"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="auth-modal-email-signup"
              />
            </div>
            <div className="bp-auth-modal-field">
              <label htmlFor="am-pw-su">Password</label>
              <input
                id="am-pw-su"
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={password}
                onChange={(e) => {
                  const v = e.target.value;
                  setPassword(v);
                  // zxcvbn runs entirely client-side; safe to compute here.
                  if (v) {
                    // dynamic import to keep initial bundle slim
                    import('zxcvbn').then(({ default: z }) => setPwScore(z(v).score));
                  } else {
                    setPwScore(0);
                  }
                }}
                placeholder="At least 10 characters"
                data-testid="auth-modal-password-signup"
              />
              <PasswordStrengthMeter password={password} minScore={MIN_PW_SCORE} />
            </div>
            <label className="bp-auth-modal-checkbox">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                data-testid="auth-modal-agree"
              />
              <span>
                I agree to BuildPilot’s{' '}
                <a href="/terms" target="_blank" rel="noreferrer noopener">Terms</a>{' & '}
                <a href="/privacy" target="_blank" rel="noreferrer noopener">Privacy Policy</a>.
              </span>
            </label>
            {error && <div className="bp-auth-modal-error" data-testid="auth-modal-error">{error}</div>}
            {info && <div className="bp-auth-modal-info" data-testid="auth-modal-info">{info}</div>}
            <CaptchaGate ref={captchaRef} action="signup" />
            <button
              type="submit"
              disabled={submitting || !configured}
              className="bp-auth-modal-primary"
              data-testid="auth-modal-submit-signup"
            >
              {submitting ? <Loader2 className="bp-auth-modal-spin" size={16} /> : 'Create account'}
            </button>
            <p className="bp-auth-modal-footer-text">
              Already have an account?{' '}
              <button type="button" onClick={() => setMode('signin')} className="bp-auth-modal-switch" data-testid="auth-modal-switch-signin">
                Sign in
              </button>
            </p>
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgot} noValidate>
            <div className="bp-auth-modal-field">
              <label htmlFor="am-email-fp">Email</label>
              <input
                id="am-email-fp"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="auth-modal-email-forgot"
              />
            </div>
            {error && <div className="bp-auth-modal-error" data-testid="auth-modal-error">{error}</div>}
            {info && <div className="bp-auth-modal-info" data-testid="auth-modal-info">{info}</div>}
            <CaptchaGate ref={captchaRef} action="forgot" />
            <button
              type="submit"
              disabled={submitting || !configured}
              className="bp-auth-modal-primary"
              data-testid="auth-modal-submit-forgot"
            >
              {submitting ? <Loader2 className="bp-auth-modal-spin" size={16} /> : 'Send reset link'}
            </button>
          </form>
        )}

        </>)}
        </div>

        {/* App download alternative - hidden on the forgot-password
            view. Lives OUTSIDE .bp-auth-modal-body so CSS grid can
            place it in the brand column on desktop (filling the
            empty space there) while still appearing at the bottom
            on mobile single-column layout. */}
        {mode !== 'forgot' && (
          <div className="bp-auth-modal-app-section">
            <p className="bp-auth-modal-app-label">Or get the BuildPilot app</p>
            <div className="bp-auth-modal-app-row">
              <a
                href="https://apps.apple.com/au/app/buildpilot/id6477372083"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download BuildPilot on the App Store"
                data-testid="auth-modal-app-store"
              >
                <img
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                  alt="Download on the App Store"
                  loading="lazy"
                  className="bp-auth-modal-apple"
                />
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.buildpilot.android&hl=en_US"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get BuildPilot on Google Play"
                data-testid="auth-modal-google-play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  loading="lazy"
                  className="bp-auth-modal-google"
                />
              </a>
            </div>
          </div>
        )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AuthModal;
